<template>
  <Map ref="googleMapRef"
       class="map"
       mapTypeId="terrain"
       :zoom="mapZoom"
       :enable-measure-tool="true"
       scrollwheel
       :center="center"
       @on-map-ready="onMapReady"
       @idle="onIdle">
    <CustomControl position="LEFT_TOP" :index="1" class="mb-5">
      <button class="custom-btn" @click="isMenuOpened = !isMenuOpened">
        {{ $t("menu") }}
      </button>
    </CustomControl>
    <CustomControl position="LEFT_TOP" :index="2">
      <button class="custom-btn" @click="isDevicesListOpened = !isDevicesListOpened">
        {{ $t("devices") }}
      </button>
    </CustomControl>
    <CustomControl v-if="showTripsSlider" position="TOP_CENTER" :index="3">
      <PositionsSlider
          class="dashboard-positions-slider"
          v-model="selectedPosition"
          :slider-positions="sliderPositions"
          @change="onPositionSliderChange"
      />
    </CustomControl>
    <VehicleMarker v-for="positionData in filteredPositions" v-bind:key="positionData.deviceId"
                   ref="vehiclePositionsRef"
                   :open-info-on-click="false"
                   :open-info-on-hover="true"
                   :auto-center="false"
                   :includeTripsButton="true"
                   :includeLoadTripsButton="true"
                   :positionData="positionData"
                   @click="onMarkerSelected"
                   @loadTrips="onLoadTrips"
                   @infoClosed="onInfoWindowClosed" />
    <MapRoute v-for="route in tripsRecordPositionsWithStops"
              :key="`dashboard-route-${route.index}`"
              ref="mapRoutes"
              :positions="route.positions"
              :stay="route.stay"
              :stops="route.stops"
              :trip="route.trip"
              :index="route.index"
              @selected="onTripSelected" />
    <CircleGeoFence v-for="geoFenceData in visibleUserGeoFences" v-bind:key="geoFenceData.id"
                    :geoFenceData="geoFenceData" :hover-enabled="false"
                    @click="geoFenceAreaClick" />
  </Map>
  <VueSidePanel
      v-model="isDevicesListOpened"
      lock-scroll
      :overlay-opacity="0"
      side="left"
      width="fit-content"
      panel-color="transparent"
      @opened="onDevicesPanelOpened">
    <DevicesCard ref="devicesCard"
                 class="m-0 mt-5 devices-container"
                 :selected-device-id="selectedDeviceId"
                 :filtered-devices="filteredDevices"
                 @on-select-device="onCardSelectDevice"
    />
  </VueSidePanel>
  <VueSidePanel
      v-model="isMenuOpened"
      lock-scroll
      :overlay-opacity="0"
      side="left">
    <div class="pt-10">
      <NavBar :support-mobile="false" />
    </div>
  </VueSidePanel>
</template>

<script>
import {dataAggregator, dataManipulator, entityDataManager, GoogleMapsManager, WorkflowManager} from "@/assets/js/data-manipulation";
import {mapGetters, mapState} from "vuex";
import Map from "@/components/pages/partials/maps/Map.vue";
import VehicleMarker from "@/components/pages/partials/maps/VehicleMarker.vue";
import CircleGeoFence from "@/components/pages/partials/maps/CircleGeoFence.vue";
import {CustomControl} from "vue3-google-map";
import {VueSidePanel} from "vue3-side-panel";
import DevicesCard from "@/components/pages/dashboard/DevicesCard.vue";
import NavBar from "@/components/NavBar.vue";
import MapRoute from "@/components/pages/partials/maps/MapRoute.vue";
import moment from "moment";
import PositionsSlider from "@/components/pages/partials/PositionsSlider.vue";

export default {
  components: {PositionsSlider, MapRoute, NavBar, DevicesCard, VueSidePanel, CircleGeoFence, VehicleMarker, Map, CustomControl},
  name: 'NewDashboardView',
  props: {
    searchToken: String,
  },
  data() {
    return {
      selectedDeviceId: null,
      isMenuOpened: false,
      isDevicesListOpened: false,
      isInfoWindowOpened: false,
      mapZoom: 15,
      center: GoogleMapsManager.defaultCenter,
      mapBounds: null,
      tripsData: null,
      tripsRecordPositions: [],
      selectedTrip: null,
      selectedPosition: null,
    };
  },
  computed: {
    ...mapState(['devices', 'groups', 'positions', 'userGeoFences', 'vehicles']),
    ...mapGetters(['isGroupShown', 'dashboardDeviceColumns', 'showZonesInDashboard']),
    filteredPositions() {
      if (!this.searchToken) {
        return this.positions;
      }

      return this.positions.filter(x => this.filteredDeviceIds.includes(x.deviceId));
    },
    currentPosition() {
      return this.filteredPositions.filter(position => position.deviceId === this.selectedDeviceId)[0] || null;
    },
    filteredDeviceIds() {
      return this.filteredDevices.map(x => x.id);
    },
    filteredDevices() {
      return this.searchToken === ''
          ? this.mappedDevices
          : dataAggregator.filterDevicesByToken(this.mappedDevices, this.searchToken);
    },
    mappedDevices() {
      return entityDataManager.mapDashboardDevices(
          this.devices,
          this.groups,
          this.vehicles,
          this.dashboardDeviceColumns,
          this.isGroupShown);
    },
    tripsRecords() {
      return this.tripsData?.trips ?? [];
    },
    tripsRecordPositionsFlat() {
      return this.tripsRecordPositions.reduce((acc, tripRecordPosition) => [...acc, ...tripRecordPosition], []);
    },
    tripsRecordPositionsWithStops() {
      return entityDataManager.getTripsRecordPositionsWithStops(
          this.tripsRecords,
          this.tripsRecordPositions,
          this.tripsRecordPositionsFlat,
          this.tripsData?.mapData?.routes,
      );
    },
    showTripsSlider() {
      return this.sliderPositions.length > 1;
    },
    sliderPositions() {
      return this.tripsRecordPositionsWithStops.reduce((acc, tripRecordPosition) => {
        const mappedPositions = tripRecordPosition.positions.map(position => {
          const timestamp = dataManipulator.iso8601DateStringToLocalMoment(position.fixTime);

          return {
            value: position.id,
            label: timestamp !== null ? timestamp.format(this.$t('settings.generic.date-time-format-moment')) : '',
            info: {
              tripIndex: tripRecordPosition.index,
            }
          }
        });

        return [...acc, ...mappedPositions];
      }, []);
    },
    visibleUserGeoFences() {
      if (!this.showZonesInDashboard) {
        return [];
      }

      if (!this.mapBounds) {
        return this.userGeoFences;
      }

      return this.userGeoFences.filter(geoFence => this.isGeoFenceVisibleInBounds(geoFence));
    }
  },
  mounted() {
    this.preselectFirstDevice();
  },
  watch: {
    filteredDevices(newDevices, oldDevices) {
      if (oldDevices.length === 0 && newDevices.length > 0) {
        this.preselectFirstDevice();
      }
    },
    filteredPositions(newPositions, oldPositions) {
      if (oldPositions.length === 0 && newPositions.length > 0) {
        this.preselectFirstDevice();
      }

      if (this.isInfoWindowOpened) {
        setTimeout(() => {
          const marker = this.getVehicleMarkerByDeviceId(this.selectedDeviceId);
          marker?.showInfoWindow();
        });
      }
    }
  },
  methods: {
    onMapReady() {
      this.preselectFirstDevice();
      this.refreshMapBounds();
    },
    onIdle() {
      this.refreshMapBounds();
      if (!this.$refs.vehiclePositionsRef?.length) {
        return;
      }

      const bounds = this.$refs.googleMapRef.map.getBounds();
      this.$refs.vehiclePositionsRef.forEach(marker => {
        if (bounds.contains(marker.position)) {
          marker.setVisible(true);
        } else {
          marker.setVisible(false);
        }
      });
    },
    onDevicesPanelOpened() {
      this.$refs.devicesCard.scrollToSelectedRow();
    },
    onCardSelectDevice(selectedDeviceId) {
      const focusSelectedDevice = this.selectedDeviceId !== null;

      this.onSelectDevice(selectedDeviceId);
      this.setCenterOnCurrentDevice();

      if (focusSelectedDevice) {
        this.mapZoom = 16;
        this.$refs.googleMapRef.map.setZoom(16);
        this.openSelectedDeviceInfoWindow();
      }
    },
    onMarkerSelected(marker) {
      this.onSelectDevice(marker.deviceId);
      this.openSelectedDeviceInfoWindow();
    },
    onInfoWindowClosed() {
      this.isInfoWindowOpened = false;
    },
    onSelectDevice(selectedDeviceId) {
      if (this.selectedDeviceId !== selectedDeviceId) {
        this.clearTripsFromMap();
      }
      this.selectedDeviceId = selectedDeviceId;
    },
    async onLoadTrips(deviceId) {
      if (!deviceId) {
        return;
      }
      this.onSelectDevice(deviceId);

      const fromDate = moment().startOf('day').toDate();
      const toDate = moment().endOf('day').toDate();
      const data = await WorkflowManager.getTripsMapDataWithoutStore(deviceId, fromDate, toDate, null);
      this.tripsData = data.tripsData;
      this.tripsRecordPositions = data.tripsRecordPositions;
      this.selectedPosition = this.sliderPositions[0]?.value ?? null;
      this.$nextTick(() => this.fitMapToTripsBounds());
    },
    clearTripsFromMap() {
      this.tripsData = null;
      this.tripsRecordPositions = [];
      this.selectedTrip = null;
      this.selectedPosition = null;
    },
    getGroupName(device) {
      return entityDataManager.getById(this.groups, device.groupId)?.name ?? '';
    },
    geoFenceAreaClick(area) {
      area?.showInfoWindow();
    },
    openSelectedDeviceInfoWindow() {
      if (!this.selectedDeviceId) {
        return;
      }

      this.$refs.vehiclePositionsRef?.forEach(marker => marker.closeInfoWindow());
      const marker = this.getVehicleMarkerByDeviceId(this.selectedDeviceId);
      marker.showInfoWindow();
      this.isInfoWindowOpened = true;
    },
    preselectFirstDevice() {
      if (this.positions.length > 0 && this.filteredDevices.length > 0) {
        this.onSelectDevice(this.filteredDevices[0].id);
        setTimeout(() => this.setCenterOnCurrentDevice());
      }
    },
    setCenterOnCurrentDevice() {
      const position = this.getPositionByDeviceId(this.selectedDeviceId);
      if (!position) {
        return;
      }

      this.center = entityDataManager.getGoogleGeoByPosition(position);
    },
    getVehicleMarkerByDeviceId(deviceId) {
      return this.$refs.vehiclePositionsRef.find(marker => marker.deviceId === deviceId)
    },
    getPositionByDeviceId(deviceId) {
      return this.filteredPositions.filter(position => position.deviceId === deviceId)[0] || null;
    },
    onTripSelected(trip) {
      this.selectTrip(trip.index);
      this.selectedRouteMouseMove(trip);
    },
    selectTrip(selectedIndex) {
      if (this.selectedTrip?.index === selectedIndex) {
        return;
      }

      this.$refs.mapRoutes?.forEach(route => {
        if (route.index === selectedIndex) {
          this.selectedTrip = route;
          return;
        }

        route.deselectPosition();
      });
    },
    selectedRouteMouseMove(route) {
      this.selectedPosition = route.selectedPosition.id;
    },
    onPositionSliderChange(value, sliderPosition) {
      if (!sliderPosition) {
        return;
      }

      const {info} = sliderPosition;
      this.selectTrip(info.tripIndex);
      if (!this.selectedTrip) {
        return;
      }

      this.selectedTrip.selectPositionById(value);
      this.selectedRouteMouseMove(this.selectedTrip);
      setTimeout(() => this.selectedTrip.initializeInfoWindow(), 10);
    },
    refreshMapBounds() {
      const bounds = this.$refs.googleMapRef?.map?.getBounds?.();
      if (!bounds) {
        return;
      }

      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();
      if (!northEast || !southWest) {
        return;
      }

      this.mapBounds = {
        north: northEast.lat(),
        east: northEast.lng(),
        south: southWest.lat(),
        west: southWest.lng(),
      };
    },
    fitMapToTripsBounds() {
      const allGoogleMapPositions = this.tripsRecordPositions
          .reduce((acc, route) => [...acc, ...route], [])
          .map(position => entityDataManager.getGoogleGeoByPosition(position))
          .filter(position => position != null);

      if (allGoogleMapPositions.length === 0) {
        return;
      }

      const bounds = GoogleMapsManager.getMapBoundsByPositions(allGoogleMapPositions);
      this.$refs.googleMapRef?.map?.fitBounds(bounds);
    },
    isGeoFenceVisibleInBounds(geoFence) {
      if (!this.mapBounds) {
        return true;
      }

      const center = geoFence?.area?.coordinates;
      const radiusMeters = geoFence?.area?.radius;
      if (!center || !Number.isFinite(radiusMeters)) {
        return false;
      }

      const lat = center.lat;
      const lng = center.lng;
      const latRadius = radiusMeters / 111320;
      const cosLat = Math.cos((lat * Math.PI) / 180);
      const lngRadius = radiusMeters / (111320 * Math.max(0.000001, Math.abs(cosLat)));

      return !(lat + latRadius < this.mapBounds.south ||
          lat - latRadius > this.mapBounds.north ||
          lng + lngRadius < this.mapBounds.west ||
          lng - lngRadius > this.mapBounds.east);
    }
  },
};
</script>

<style scoped lang="scss">
.map {
  width: 100%;
  height: calc(100vh - 105px);
  border-radius: 15px;
}

.dashboard-positions-slider {
  width: min(900px, calc(100vw - 120px));
  margin-top: 15px;
  padding: 12px 16px !important;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 2px 10px rgba(32, 33, 36, 0.2);
}

.devices-container {
  max-height: 95% !important;

  :deep(table.table-tracking) {
    thead {
      tr > th {
        padding-left: 5px;
      }
    }
    tbody {
      tr > td {
        padding-left: 5px;
      }
    }
  }
}

</style>
